import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { unique } from "@lib/utils";
import { UserError } from "@models/user";
import { useUserStore } from "@providers/auth";
import axios from "axios";
import { useAtom } from "jotai";
import { SubmitHandler, useForm } from "react-hook-form";
import { emailModalAtom, ticketingModalOpenAtom } from "utils/atoms";
import { transformEmailAddr } from "utils/misc";

const validateAndUpdateEmail = async (
  emailAddress: string,
  userId: string
): Promise<{ [x: string]: any }> => {
  try {
    const config = {
      method: "post",
      url: `/api/users/${userId}/email`,
      data: {
        email: transformEmailAddr(emailAddress),
      },
    };
    const res = await axios(config);
    //const res: { [index: string]: any } = await new Promise(resolve => {
    //  setTimeout(() => resolve({ status: 200, data: { success: true } }), 1000);
    //});
    return res.data;
  } catch (err: any) {
    //console.log(err);
    return err.response.data;
  }
};

type FormValues = {
  emailAddress: string;
};

const EmailModal = ({ t }: { t: any }) => {
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const size = isMobile ? "full" : "md";
  const user = useUserStore((state) => state.user);
  const [emailModal, setEmailModal] = useAtom(emailModalAtom);
  const [, setTicketingModal] = useAtom(ticketingModalOpenAtom);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = async (values: FormValues) => {
    if (!user) {
      console.error("user not found");
      return;
    }
    const result = await validateAndUpdateEmail(values.emailAddress, user.id);
    //console.log("validateAndUpdateEmail result", result);
    if (result.success) {
      user.email = values.emailAddress;
      user.identifiers = unique([...user.identifiers, user.email]);
      useUserStore.setState({ user });
      //console.log(user);
      setEmailModal(false);
      return setTicketingModal(true);
    }
    switch (result.error) {
      case UserError.EMAIL_INUSE:
        setError("emailAddress", {
          type: "invalid",
          message: t("emailRegistered"),
        });
        break;
      default:
        console.error("unknown error: ", result.error?.code);
    }
  };
  return (
    <Modal
      size={size}
      isOpen={emailModal}
      onClose={() => setEmailModal(false)}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        py="5rem"
        px="3.75rem"
        h={{ sm: "28rem" }}
        minWidth={{ sm: "40rem" }}
      >
        <ModalCloseButton />
        <div className="flex flex-col items-center">
          <div className="flex flex-col items-start">
            <Text fontSize="4xl"> {t("oneMoreStep")}</Text>
            <Text fontSize="md" color="neutral.300">
              {t("connectEmail")}
            </Text>
          </div>
        </div>

        <form className="flex flex-col my-8" onSubmit={handleSubmit(onSubmit)}>
          <FormControl isInvalid={Boolean(errors["emailAddress"])}>
            <FormLabel htmlFor={"emailAddress"} variant="custom">
              <p className="text-primary-200">
                <span>{t("emailAddress")}</span>
              </p>
            </FormLabel>
            <Input
              type={"text"}
              placeholder={"example@gmail.com"}
              {...register("emailAddress", {
                required: `${t("emailAddress")} ${t("required")}`,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("invalidEmail"),
                },
              })}
            />
            <FormErrorMessage>
              {errors["emailAddress"]?.message}
            </FormErrorMessage>
          </FormControl>

          <Button
            mt={8}
            h="3.5rem"
            colorScheme="teal"
            isLoading={isSubmitting}
            type="submit"
          >
            {t("signIn")}
          </Button>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default EmailModal;
